import React from 'react';
import { Download, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { ServerInfoPanel } from './ServerInfoPanel';
import { ArchitectureEnvironment } from './ArchitectureEnvironment';
import { SunoSyncEngine } from './SunoSyncEngine';
import { useAuth } from '@/context/AuthContext';

interface SystemConfigTabProps {
  sunoCookie: string;
  onSave: (settings: { sunoCookie: string; sunoProxyUrl: string }) => void;
  onClose: () => void;
}

export function SystemConfigTab({ sunoCookie, onSave, onClose }: SystemConfigTabProps): React.ReactElement {
  const { isAdmin } = useAuth();
  const handleSaveAndClose = (): void => {
    onClose();
  };

  return (
    <div className="space-y-8">
      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-50">System Configuration</h2>
          <p className="text-sm text-slate-400 mt-1">Configure architecture environment and sync engines</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Download size={12} />}
          className="text-[11px] uppercase tracking-widest"
        >
          Settings Migration (Export/Import)
        </Button>
      </div>

      {/* Server Hardware Info — admin only */}
      {isAdmin && <ServerInfoPanel />}

      {/* Configuration Sections - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Architecture Environment */}
        <div className="lg:col-span-6">
          <ArchitectureEnvironment />
        </div>

        {/* Right Column: Suno Sync Engine */}
        <div className="lg:col-span-6">
          <SunoSyncEngine sunoCookie={sunoCookie} onSave={onSave} />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-slate-800">
        <Button
          variant="primary"
          size="lg"
          leftIcon={<Save size={16} />}
          onClick={handleSaveAndClose}
          className="px-8 uppercase tracking-widest shadow-xl shadow-amber-900/20"
        >
          Save & Close
        </Button>
      </div>
    </div>
  );
}
